import type { VectorNetwork } from "@/document/model/vectorNetwork";
import type { Point } from "@/document/operations/pathGeometry";
import { formatCacheNumber } from "./cache";
import type { CachedPathMesh } from "./types";

export type NetworkMesh = CachedPathMesh & { closed: boolean; sharedKey: string; origin: Point };

export interface GeometryPoolStats {
  hits: number;
  misses: number;
  entries: number;
}

const DEFAULT_MAX_ENTRIES = 4096;

/**
 * Shared local-space geometry pool (P6.3: stays in TS wrapping Wasm/TS mesh outputs).
 * Keys are translation-normalized and ID-independent so duplicated shapes share one mesh.
 * Do not port the pool into the Rust crate until profiling shows FFI/cache wins.
 */
export class GeometryPool {
  private readonly networkMeshes = new Map<string, NetworkMesh>();
  private readonly pathMeshes = new Map<string, CachedPathMesh>();
  private readonly lruKeys: string[] = [];
  private hits = 0;
  private misses = 0;

  constructor(private readonly maxEntries = DEFAULT_MAX_ENTRIES) {}

  stats(): GeometryPoolStats {
    return {
      hits: this.hits,
      misses: this.misses,
      entries: this.networkMeshes.size + this.pathMeshes.size,
    };
  }

  resetStats(): void {
    this.hits = 0;
    this.misses = 0;
  }

  getNetworkMesh(sharedKey: string): NetworkMesh | undefined {
    const mesh = this.networkMeshes.get(sharedKey);
    if (!mesh) {
      this.misses += 1;
      return undefined;
    }
    this.hits += 1;
    this.touch(`network:${sharedKey}`);
    return mesh;
  }

  setNetworkMesh(sharedKey: string, mesh: NetworkMesh): void {
    this.networkMeshes.set(sharedKey, mesh);
    this.touch(`network:${sharedKey}`);
    this.evictIfNeeded();
  }

  getPathMesh(sharedKey: string): CachedPathMesh | undefined {
    const mesh = this.pathMeshes.get(sharedKey);
    if (!mesh) {
      this.misses += 1;
      return undefined;
    }
    this.hits += 1;
    this.touch(`path:${sharedKey}`);
    return mesh;
  }

  setPathMesh(sharedKey: string, mesh: CachedPathMesh): void {
    this.pathMeshes.set(sharedKey, mesh);
    this.touch(`path:${sharedKey}`);
    this.evictIfNeeded();
  }

  clear(): void {
    this.networkMeshes.clear();
    this.pathMeshes.clear();
    this.lruKeys.length = 0;
    this.resetStats();
  }

  private touch(key: string): void {
    const index = this.lruKeys.indexOf(key);
    if (index >= 0) {
      this.lruKeys.splice(index, 1);
    }
    this.lruKeys.push(key);
  }

  private evictIfNeeded(): void {
    while (this.networkMeshes.size + this.pathMeshes.size > this.maxEntries && this.lruKeys.length > 0) {
      const oldest = this.lruKeys.shift();
      if (!oldest) {
        return;
      }
      if (oldest.startsWith("network:")) {
        this.networkMeshes.delete(oldest.slice("network:".length));
      } else if (oldest.startsWith("path:")) {
        this.pathMeshes.delete(oldest.slice("path:".length));
      }
    }
  }
}

let sharedGeometryPool = new GeometryPool();

export function getGeometryPool(): GeometryPool {
  return sharedGeometryPool;
}

export function resetGeometryPoolForTests(pool = new GeometryPool()): GeometryPool {
  sharedGeometryPool = pool;
  return sharedGeometryPool;
}

export function networkOrigin(network: VectorNetwork): Point {
  if (network.vertices.length === 0) {
    return { x: 0, y: 0 };
  }

  let minX = network.vertices[0]!.x;
  let minY = network.vertices[0]!.y;
  for (let index = 1; index < network.vertices.length; index += 1) {
    const vertex = network.vertices[index]!;
    minX = Math.min(minX, vertex.x);
    minY = Math.min(minY, vertex.y);
  }
  return { x: minX, y: minY };
}

/** Translation-normalized, ID-independent geometry key for vector networks. */
export function sharedNetworkGeometryKey(network: VectorNetwork): string {
  const origin = networkOrigin(network);
  const vertexIndexById = new Map(network.vertices.map((vertex, index) => [vertex.id, index]));
  const edgeIndexById = new Map(network.edges.map((edge, index) => [edge.id, index]));

  return [
    network.fillRule,
    ...network.vertices.map(vertex => [
      formatCacheNumber(vertex.x - origin.x),
      formatCacheNumber(vertex.y - origin.y),
      vertex.handleMode ?? "none",
      vertex.strokeJoin ?? "miter",
      formatCacheNumber(vertex.cornerRadius ?? 0),
    ].join(",")),
    ...network.edges.map(edge => [
      vertexIndexById.get(edge.from.vertexId) ?? -1,
      edge.from.handle
        ? `${formatCacheNumber(edge.from.handle.x - origin.x)},${formatCacheNumber(edge.from.handle.y - origin.y)}`
        : "",
      vertexIndexById.get(edge.to.vertexId) ?? -1,
      edge.to.handle
        ? `${formatCacheNumber(edge.to.handle.x - origin.x)},${formatCacheNumber(edge.to.handle.y - origin.y)}`
        : "",
      edge.directed ? "1" : "0",
    ].join(",")),
    ...network.regions.map(region => [
      region.edgeIds.map(edgeId => edgeIndexById.get(edgeId) ?? -1).join(":"),
      region.filled === false ? "0" : "1",
      region.fillStyleId ?? "",
    ].join(",")),
  ].join("|");
}

export function translateVectorNetwork(network: VectorNetwork, dx: number, dy: number): VectorNetwork {
  if (dx === 0 && dy === 0) {
    return network;
  }

  return {
    ...network,
    vertices: network.vertices.map(vertex => ({
      ...vertex,
      x: vertex.x + dx,
      y: vertex.y + dy,
    })),
    edges: network.edges.map(edge => ({
      ...edge,
      from: {
        vertexId: edge.from.vertexId,
        handle: edge.from.handle
          ? { x: edge.from.handle.x + dx, y: edge.from.handle.y + dy }
          : undefined,
      },
      to: {
        vertexId: edge.to.vertexId,
        handle: edge.to.handle
          ? { x: edge.to.handle.x + dx, y: edge.to.handle.y + dy }
          : undefined,
      },
    })),
    regions: network.regions.map(region => ({ ...region, edgeIds: [...region.edgeIds] })),
    fillOverrideTable: network.fillOverrideTable,
    fillRule: network.fillRule,
  };
}

export function translateNumberVertices(vertices: Float32Array, dx: number, dy: number): Float32Array {
  if ((dx === 0 && dy === 0) || vertices.length === 0) {
    return vertices;
  }

  const translated = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    translated[index] = vertices[index]! + dx;
    translated[index + 1] = vertices[index + 1]! + dy;
  }
  return translated;
}

export function translateContours(contours: Point[][], dx: number, dy: number): Point[][] {
  if (dx === 0 && dy === 0) {
    return contours;
  }

  return contours.map(contour => contour.map(point => ({ x: point.x + dx, y: point.y + dy })));
}

export function translateNetworkMesh(localMesh: NetworkMesh, origin: Point): NetworkMesh {
  const dx = origin.x;
  const dy = origin.y;
  if (dx === 0 && dy === 0) {
    return {
      ...localMesh,
      origin,
      strokeVerticesByWidth: new Map(localMesh.strokeVerticesByWidth),
    };
  }

  return {
    key: localMesh.key,
    sharedKey: localMesh.sharedKey,
    origin,
    closed: localMesh.closed,
    contours: translateContours(localMesh.contours, dx, dy),
    fillContours: localMesh.fillContours ? translateContours(localMesh.fillContours, dx, dy) : undefined,
    vertices: translateNumberVertices(localMesh.vertices, dx, dy),
    fillVertices: localMesh.fillVertices ? translateNumberVertices(localMesh.fillVertices, dx, dy) : undefined,
    fillVertexChunks: localMesh.fillVertexChunks?.map(chunk => translateNumberVertices(chunk, dx, dy)),
    fillGroups: localMesh.fillGroups?.map(group => ({
      ...group,
      contours: translateContours(group.contours, dx, dy),
      vertices: translateNumberVertices(group.vertices, dx, dy),
      edgeRefs: group.edgeRefs.map(refs => refs.map(ref => ({ ...ref }))),
      edgeIds: [...group.edgeIds],
    })),
    strokeVerticesByWidth: new Map(
      [...localMesh.strokeVerticesByWidth.entries()].map(([key, vertices]) => [
        key,
        translateNumberVertices(vertices, dx, dy),
      ]),
    ),
  };
}

export function contoursOrigin(contours: Point[][]): Point {
  let minX = Infinity;
  let minY = Infinity;
  let found = false;
  for (const contour of contours) {
    for (const point of contour) {
      found = true;
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
    }
  }
  return found ? { x: minX, y: minY } : { x: 0, y: 0 };
}

export function sharedPathGeometryKey(contours: Point[][]): string {
  const origin = contoursOrigin(contours);
  return contours
    .map(contour => contour
      .map(point => `${formatCacheNumber(point.x - origin.x)},${formatCacheNumber(point.y - origin.y)}`)
      .join(";"))
    .join("/");
}

export function translatePathMesh(localMesh: CachedPathMesh, origin: Point): CachedPathMesh {
  const dx = origin.x;
  const dy = origin.y;
  if (dx === 0 && dy === 0) {
    return {
      ...localMesh,
      strokeVerticesByWidth: new Map(localMesh.strokeVerticesByWidth),
    };
  }

  return {
    key: localMesh.key,
    contours: translateContours(localMesh.contours, dx, dy),
    fillContours: localMesh.fillContours ? translateContours(localMesh.fillContours, dx, dy) : undefined,
    vertices: translateNumberVertices(localMesh.vertices, dx, dy),
    fillVertices: localMesh.fillVertices ? translateNumberVertices(localMesh.fillVertices, dx, dy) : undefined,
    fillVertexChunks: localMesh.fillVertexChunks?.map(chunk => translateNumberVertices(chunk, dx, dy)),
    fillGroups: localMesh.fillGroups?.map(group => ({
      ...group,
      contours: translateContours(group.contours, dx, dy),
      vertices: translateNumberVertices(group.vertices, dx, dy),
      edgeRefs: group.edgeRefs.map(refs => refs.map(ref => ({ ...ref }))),
      edgeIds: [...group.edgeIds],
    })),
    strokeVerticesByWidth: new Map(
      [...localMesh.strokeVerticesByWidth.entries()].map(([key, vertices]) => [
        key,
        translateNumberVertices(vertices, dx, dy),
      ]),
    ),
  };
}
